/**
 * useBulkResolutionCompletion — post-resolve advance navigation for the AI
 * Actions list. Extracted from useBulkResolution alongside
 * useBulkResolutionCache and useBulkResolutionSelection.
 *
 * Single-action resolutions (approve/dismiss one action, not a bulk group)
 * advance the user to the pre-captured next action in display order on
 * desktop layouts. Bulk resolutions leave the list in place.
 */
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface BulkResolutionCompletion {
  /** Navigates to `nextActionId` on desktop (>=1024px) layouts only. No-op
      when nextActionId is null/undefined or the viewport is narrower. */
  advanceToNext: (
    orgId: number,
    nextActionId: number | null | undefined
  ) => void;
}

export function useBulkResolutionCompletion(): BulkResolutionCompletion {
  const navigate = useNavigate();
  const location = useLocation();

  const advanceToNext = useCallback(
    (orgId: number, nextActionId: number | null | undefined) => {
      // Single-action advance: navigate to the pre-captured next action on
      // desktop (≥1024px). The id was captured BEFORE the await so cache
      // mutations during the await do not cause a stale lookup (spec step 1
      // sharp edge). `backPath` (not the legacy `view` key) is what the
      // redesigned ActionDetailPage reads for its back chevron — the same
      // state every row click in this component already sends. Sending
      // `view` here left the chevron with no origin to return to.
      if (nextActionId != null && window.innerWidth >= 1024) {
        navigate(`/${orgId}/actions/${nextActionId}`, {
          state: { backPath: location.pathname },
        });
      }
    },
    [navigate, location.pathname]
  );

  return useMemo(() => ({ advanceToNext }), [advanceToNext]);
}
